/**
 * @fileoverview Contact Fields manager
 *
 * Field-Presets-style manager for the org's per-category custom firm-contact
 * field definitions. Each firm-contact category (Banks, Regulators, Firm
 * Members, …) gets a card; every contact in that category shares the fields,
 * and their values pull through onto day-one documents.
 *
 * Visual language mirrors Settings → Fields (Field Presets) for consistency:
 * a header card, then one card per category with an "Add field" action and a
 * divided list of field rows.
 *
 * Admin/owner only — `canEdit` gates writes; mutations enforce the same rule.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ArrowDown,
  ArrowUp,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  FIRM_CATEGORY_META,
  FIRM_CATEGORY_ORDER,
  type FirmContactCategory,
} from "./types";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

const CATEGORY_DESCRIPTIONS: Record<FirmContactCategory, string> = {
  bank: "Shared fields for every bank contact",
  regulator: "Shared fields for every regulator contact",
  professional: "Shared fields for every professional contact",
  org_member: "Shared fields for your firm's members (e.g. Role, Rate)",
  referrer: "Shared fields for every referrer contact",
  other: "Shared fields for other firm contacts",
};

type FieldType = "text" | "number" | "currency" | "date" | "select";

const FIELD_TYPE_OPTIONS: ReadonlyArray<{ value: FieldType; label: string }> = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "date", label: "Date" },
  { value: "select", label: "Select" },
];

interface FieldDefinition {
  _id: Id<"firmContactFieldDefinitions">;
  category: FirmContactCategory;
  key: string;
  label: string;
  fieldType: FieldType;
  selectOptions?: string[];
  placeholder?: string;
  sortOrder: number;
}

export function FirmContactFieldsManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: definitions } = useQuery({
    ...convexQuery(
      api.contacts.fieldDefinitions.listFirmContactFieldDefinitions,
      { orgId },
    ),
  });

  const byCategory = useMemo(() => {
    const map = new Map<FirmContactCategory, FieldDefinition[]>();
    for (const def of definitions ?? []) {
      const list = map.get(def.category) ?? [];
      list.push(def);
      map.set(def.category, list);
    }
    return map;
  }, [definitions]);

  const total = definitions?.length ?? 0;

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Header */}
      <div
        className={`flex items-start justify-between gap-6 p-6 ${GROUP_CARD_CLASS}`}
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-primary">Contact Fields</h3>
          <p className="mt-1 text-sm text-primary/60">
            Define extra fields per contact category. Every contact in a
            category shares the same fields, and their values pull through onto
            day-one documents — just like a Firm Contact's name or email.
          </p>
        </div>
        <Badge variant="secondary" className="shrink-0">
          {total} {total === 1 ? "field" : "fields"}
        </Badge>
      </div>

      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need owner or admin access to manage contact fields.
            </p>
          </div>
        </div>
      )}

      {FIRM_CATEGORY_ORDER.map((category) => (
        <CategoryCard
          key={category}
          orgId={orgId}
          category={category}
          canEdit={canEdit}
          definitions={byCategory.get(category) ?? []}
        />
      ))}
    </div>
  );
}

function CategoryCard({
  orgId,
  category,
  canEdit,
  definitions,
}: {
  orgId: Id<"organizations">;
  category: FirmContactCategory;
  canEdit: boolean;
  definitions: FieldDefinition[];
}) {
  const [showNewForm, setShowNewForm] = useState(false);
  const meta = FIRM_CATEGORY_META[category];
  const Icon = meta.icon;

  return (
    <div className={GROUP_CARD_CLASS}>
      {/* Category header */}
      <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary/5">
            <Icon className="size-4 text-primary/60" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-primary">{meta.label}</h4>
            <p className="text-xs text-primary/40">
              {CATEGORY_DESCRIPTIONS[category]}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-primary/40">
            {definitions.length} {definitions.length === 1 ? "field" : "fields"}
          </span>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewForm(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              Add field
            </Button>
          )}
        </div>
      </div>

      {/* Field rows */}
      <div className="divide-y divide-border/30">
        {definitions.map((def, index) => (
          <DefinitionRow
            key={def._id}
            orgId={orgId}
            category={category}
            def={def}
            canEdit={canEdit}
            isFirst={index === 0}
            isLast={index === definitions.length - 1}
            orderedIds={definitions.map((d) => d._id)}
            index={index}
          />
        ))}

        {definitions.length === 0 && !showNewForm && (
          <div className="px-6 py-8 text-center">
            <p className="text-sm text-primary/40">
              No fields yet. Add one to start carrying{" "}
              {meta.label.toLowerCase()} detail onto day-one documents.
            </p>
          </div>
        )}

        {showNewForm && (
          <NewFieldForm
            orgId={orgId}
            category={category}
            onClose={() => setShowNewForm(false)}
          />
        )}
      </div>
    </div>
  );
}

function DefinitionRow({
  orgId,
  category,
  def,
  canEdit,
  isFirst,
  isLast,
  orderedIds,
  index,
}: {
  orgId: Id<"organizations">;
  category: FirmContactCategory;
  def: FieldDefinition;
  canEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  orderedIds: Array<Id<"firmContactFieldDefinitions">>;
  index: number;
}) {
  const [editing, setEditing] = useState(false);
  const update = useConvexMutation(
    api.contacts.fieldDefinitions.updateFirmContactFieldDefinition,
  );
  const remove = useConvexMutation(
    api.contacts.fieldDefinitions.deleteFirmContactFieldDefinition,
  );
  const reorder = useConvexMutation(
    api.contacts.fieldDefinitions.reorderFirmContactFieldDefinitions,
  );

  const move = (direction: -1 | 1) => {
    const next = [...orderedIds];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder({ orgId, category, orderedIds: next }).catch((e: Error) =>
      toast.error(e.message || "Could not reorder fields."),
    );
  };

  if (editing) {
    return (
      <div className="px-6 py-4">
        <FieldForm
          initial={{
            label: def.label,
            fieldType: def.fieldType,
            placeholder: def.placeholder ?? "",
            optionsText: (def.selectOptions ?? []).join("\n"),
          }}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (state) => {
            await update({
              definitionId: def._id,
              patch: {
                label: state.label.trim(),
                fieldType: state.fieldType,
                placeholder: state.placeholder.trim() || undefined,
                selectOptions:
                  state.fieldType === "select"
                    ? parseOptions(state.optionsText)
                    : undefined,
              },
            });
            setEditing(false);
          }}
        />
      </div>
    );
  }

  const typeLabel = FIELD_TYPE_OPTIONS.find(
    (t) => t.value === def.fieldType,
  )?.label;

  return (
    <div className="flex items-center gap-3 px-6 py-3">
      {canEdit && (
        <div className="flex flex-col">
          <button
            type="button"
            disabled={isFirst}
            onClick={() => move(-1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move up"
          >
            <ArrowUp className="size-3" />
          </button>
          <button
            type="button"
            disabled={isLast}
            onClick={() => move(1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move down"
          >
            <ArrowDown className="size-3" />
          </button>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm text-primary">{def.label}</div>
        <div className="text-xs text-primary/40">
          {typeLabel}
          {def.fieldType === "select" && def.selectOptions?.length
            ? ` · ${def.selectOptions.join(", ")}`
            : ""}
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-destructive hover:text-destructive"
            onClick={() => {
              remove({ definitionId: def._id }).catch((e: Error) =>
                toast.error(e.message || "Could not delete field."),
              );
            }}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function NewFieldForm({
  orgId,
  category,
  onClose,
}: {
  orgId: Id<"organizations">;
  category: FirmContactCategory;
  onClose: () => void;
}) {
  const create = useConvexMutation(
    api.contacts.fieldDefinitions.createFirmContactFieldDefinition,
  );
  return (
    <div className="bg-muted/20 px-6 py-4">
      <FieldForm
        initial={{
          label: "",
          fieldType: "text",
          placeholder: "",
          optionsText: "",
        }}
        submitLabel="Add field"
        onCancel={onClose}
        onSubmit={async (state) => {
          await create({
            orgId,
            category,
            label: state.label.trim(),
            fieldType: state.fieldType,
            placeholder: state.placeholder.trim() || undefined,
            selectOptions:
              state.fieldType === "select"
                ? parseOptions(state.optionsText)
                : undefined,
          });
          onClose();
        }}
      />
    </div>
  );
}

interface FieldFormState {
  label: string;
  fieldType: FieldType;
  placeholder: string;
  optionsText: string;
}

function FieldForm({
  initial,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: FieldFormState;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (state: FieldFormState) => Promise<void>;
}) {
  const [state, setState] = useState<FieldFormState>(initial);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!state.label.trim()) {
      toast.error("Field label is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(state);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not save field."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            Label
          </Label>
          <Input
            value={state.label}
            autoFocus
            onChange={(e) => setState({ ...state, label: e.target.value })}
            placeholder="Role, Rate, Experience, BSB…"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            Type
          </Label>
          <Select
            value={state.fieldType}
            onValueChange={(v) =>
              setState({ ...state, fieldType: v as FieldType })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPE_OPTIONS.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Placeholder (optional)
        </Label>
        <Input
          value={state.placeholder}
          onChange={(e) => setState({ ...state, placeholder: e.target.value })}
          placeholder="Hint shown in the empty input"
        />
      </div>
      {state.fieldType === "select" && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            Options (one per line)
          </Label>
          <textarea
            value={state.optionsText}
            onChange={(e) =>
              setState({ ...state, optionsText: e.target.value })
            }
            placeholder={"Partner\nSenior\nAnalyst"}
            className="min-h-[80px] rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="gap-1">
          <X className="size-3.5" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting}
          className="gap-1.5"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function parseOptions(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((o) => o.trim())
    .filter((o) => o.length > 0);
}
